<template>
  <vs-popup class="add-additional-cost-popup"
            id="popup"
            :title="`UPDATE INVOICE (${invoice_number || product_order_code})`"
            @close="open = false"
            :active.sync="open"
            :buttons-hidden="true">
    <vx-card no-shadow>
      <vs-alert class="h-auto mb-8 text-lg" color="danger" v-if="!is_invoice_updatable && selectedItems.length > 0">
        Please configure the nominal code settings before updating the invoice.
      </vs-alert>
      <ValidationObserver ref="form" v-slot="{ handleSubmit }">
        <form @submit.prevent="handleSubmit(onClickUpdateInvoice)" autocomplete="off">

          <div class="vx-row mb-3">
            <div class="vx-col md:w-full w-full md:mb-0">
              <ValidationProvider vid="date" name="date" rules="required" v-slot="{ errors }">
                <small class="label">Invoice date <span class="text-danger">*</span></small>
                <flat-pickr style="display: none" :config="{ dateFormat: 'd/m/Y'}" class="w-full" v-model="createForm.date"/>
                <flat-pickr :config="{ dateFormat: 'd/m/Y'}" class="w-full" v-model="createForm.date"/>
                <span class="text-danger text-xs mt-0">{{ errors[0] }}</span>
              </ValidationProvider>
            </div>
          </div>

          <div class="vx-row mb-4">
            <div class="vx-col md:w-full w-full md:mb-0">
              <ValidationProvider vid="note" name="note" rules="" v-slot="{ errors }">
                <small class="label">Note</small>
                <vs-textarea class="mb-0" v-model="createForm.note" height="70px"/>
                <span class="text-danger text-xs mt-0">{{ errors[0] }}</span>
              </ValidationProvider>
            </div>
          </div>

          <vs-table v-if="is_table_visible" :hoverFlat="true" :onlyClickCheckbox="true" multiple v-model="selectedItems" class="mt-8" :data="formulation_items">

            <template slot="header">
              <h5 class="mb-4">Select items</h5>
            </template>

            <template slot="thead">
              <vs-th width="15%">ITS PRODUCT CODE</vs-th>
              <vs-th>FG TYPE</vs-th>
              <vs-th width="15%">TOTAL ITEM QUANTITY (GRAM)</vs-th>
              <vs-th width="15%">REMAINING QUANTITY (GRAM)</vs-th>
              <vs-th width="10%">SELECTED QUANTITY (GRAM)</vs-th>
              <vs-th width="15%">UNIT PRICE Excl VAT ({{ account_currency }}/KG)</vs-th>
              <vs-th width="15%">TOTAL PRICE Excl VAT ({{ account_currency }})</vs-th>
              <vs-th width="10%">TAX CODE</vs-th>
              <vs-th width="10%">SAGE DEPARTMENT</vs-th>
              <vs-th width="15%">NOMINAL CODE</vs-th>
            </template>

            <template slot-scope="{data}">
              <vs-tr v-if="tr_item.remaining_to_be_quantity > 0 || isItemSelected(tr_item)" :data="tr_item" :key="index_item" v-for="(tr_item, index_item) in data">

                <vs-td :data="tr_item.formulation_code">
                  {{ tr_item.formulation_code }}
                </vs-td>

                <vs-td :data="tr_item.type">
                  {{ tr_item.type }}
                </vs-td>

                <vs-td :data="tr_item.total_quantity">
                  {{ tr_item.total_quantity }}
                </vs-td>

                <vs-td :data="tr_item.remaining_to_be_quantity">
                  {{ tr_item.remaining_to_be_quantity }}
                </vs-td>

                <vs-td :data="tr_item.selected_quantity">
                  <ValidationProvider :vid="index_item + 'selected_quantity'" :name="index_item + 'selected_quantity'" :rules="`required|min_qty:${tr_item.remaining_to_be_quantity}`" v-slot="{ errors }">
                    <vs-input type="text" v-model="tr_item.selected_quantity" class="w-full"
                              :name="index_item+'selected_quantity'" :danger="!!errors[0]" :danger-text="errors[0]" val-icon-danger="icon-x" val-icon-pack="feather"/>
                  </ValidationProvider>
                </vs-td>

                <vs-td :data="tr_item.price">
                  {{ tr_item.price | format_price(fx_value) }}
                </vs-td>

                <vs-td :data="tr_item.selected_quantity">
                  {{ (tr_item.selected_quantity ? calculateNetValue(tr_item) : '0.00') }}
                </vs-td>

                <vs-td>
                  <vs-dropdown>
                    <a class="flex items-center cursor-pointer">
                      {{ tr_item.tax_code ? `${tr_item.tax_code} - ${tr_item.tax_percent}%` : 'SELECT' }}
                      <i class="material-icons"> expand_more </i>
                    </a>
                    <vs-dropdown-menu style="z-index: 99999">
                      <vs-dropdown-item @click="onClickTaxCode(tax_code.code, tax_code.percent, tr_item)" v-for="(tax_code, index) in tax_codes" :key="index">
                        <span>{{ tax_code.code }} - {{ tax_code.percent }} % </span>
                      </vs-dropdown-item>
                    </vs-dropdown-menu>
                  </vs-dropdown>
                </vs-td>

                <vs-td>
                  <select v-model="tr_item.sage_department_id" class="default-dropdown">
                    <option :value="null">SELECT</option>
                    <option v-for="(department, index) in sage_departments" :key="index" :value="department.id">
                      {{ department.name }}
                    </option>
                  </select>
                </vs-td>

                <vs-td>
                  <select v-model="tr_item.sage_nominal_code_id" class="default-dropdown">
                    <option :value="null">SELECT</option>
                    <option v-for="(nominal, index) in nominal_codes" :key="index" :value="nominal.id">
                      {{ nominal.code }}
                    </option>
                  </select>
                </vs-td>

              </vs-tr>
            </template>

          </vs-table>

          <vs-table noDataText="Click add to add additional cost" class="mt-8" :data="createForm.add_cost">

            <template slot="header">
              <h5 class="mb-4">Additional cost</h5>
              <div class="flex flex-wrap items-center justify-end mt-8">
                <vs-button @click.stop="onClickAddAddCost()" size="small" class="ml-auto mt-2 mb-4">Add</vs-button>
              </div>
            </template>

            <template slot="thead">
              <vs-th>NAME</vs-th>
              <vs-th>AMOUNT Excl VAT (GBP)</vs-th>
              <vs-th v-if="!__isCurrencyGbp(account_currency)">AMOUNT Excl VAT ({{ account_currency }})</vs-th>
              <vs-th>VAT PERCENTAGE (%)</vs-th>
              <vs-th>NOMINAL CODE</vs-th>
              <vs-th>SAGE DEPARTMENT</vs-th>
              <vs-th></vs-th>
            </template>

            <template slot-scope="{data}">
              <vs-tr :key="index_cost" v-for="(tr_cost, index_cost) in data">

                <vs-td :data="tr_cost.name">
                  <ValidationProvider :vid="index_cost+'name'" :name="index_cost+'name'"
                                      rules="required" v-slot="{ errors }">
                    <vs-input type="text" v-model="tr_cost.name"
                              class="w-full"
                              :name="index_cost+'name'" :danger="!!errors[0]" :danger-text="errors[0]"
                              val-icon-danger="icon-x"
                              val-icon-pack="feather"/>
                  </ValidationProvider>
                </vs-td>

                <vs-td :data="tr_cost.amount">
                  <ValidationProvider :vid="index_cost+'amount'" :name="index_cost+'amount'"
                                      rules="required" v-slot="{ errors }">
                    <vs-input type="text" v-model="tr_cost.amount"
                              class="w-full"
                              :name="index_cost+'amount'" :danger="!!errors[0]" :danger-text="errors[0]"
                              val-icon-danger="icon-x"
                              val-icon-pack="feather"/>
                  </ValidationProvider>
                </vs-td>

                <vs-td v-if="!__isCurrencyGbp(account_currency)">
                  {{ tr_cost.amount | format_price(fx_value) }}
                </vs-td>

                <vs-td :data="tr_cost.vat_percent">
                  <ValidationProvider :vid="index_cost+'vat_percent'" :name="index_cost+'vat_percent'"
                                      rules="required" v-slot="{ errors }">
                    <vs-input type="text" v-model="tr_cost.vat_percent"
                              class="w-full"
                              :name="index_cost+'vat_percent'" :danger="!!errors[0]" :danger-text="errors[0]"
                              val-icon-danger="icon-x"
                              val-icon-pack="feather"/>
                  </ValidationProvider>
                </vs-td>

                <vs-td>
                  <select v-model="tr_cost.sage_nominal_code_id" class="default-dropdown">
                    <option :value="null">SELECT</option>
                    <option v-for="option in nominal_codes" :key="option.id" :value="option.id">
                      {{ option.code }}
                    </option>
                  </select>
                </vs-td>

                <vs-td>
                  <select v-model="tr_cost.sage_department_id" class="default-dropdown">
                    <option :value="null">SELECT</option>
                    <option v-for="option in sage_departments" :key="option.id" :value="option.id">
                      {{ option.name }}
                    </option>
                  </select>
                </vs-td>

                <vs-td>
                  <div class="vx-row">
                    <vx-tooltip class="ml-2" text="Remove" position="top">
                      <vs-button @click.stop="onClickRemoveAddCost(index_cost)"
                                 size="small" color="danger" type="border" icon-pack="feather" icon="icon-trash"></vs-button>
                    </vx-tooltip>
                  </div>
                </vs-td>

              </vs-tr>
            </template>

          </vs-table>

          <!-- Save & Cancel Button -->
          <div class="flex flex-wrap items-center justify-end mt-8">
            <vs-button @click="open = false" class="mr-3 mt-2" type="border" color="danger">Cancel</vs-button>
            <vs-button :disabled="!is_invoice_updatable" button="submit" class="mt-2">Save Changes</vs-button>
          </div>
        </form>
      </ValidationObserver>
    </vx-card>
  </vs-popup>
</template>

<script>
  import { RepositoryFactory } from '@/repository/RepositoryFactory'

  import SuccessMessage from '@/mixins/SuccessMessage'
  import ErrorMessages from '@/mixins/ErrorMessages'
  import Calculations from '@/mixins/Calculations'
  import flatPickr from 'vue-flatpickr-component'
  import 'flatpickr/dist/flatpickr.css'

  const CustomerRepository = RepositoryFactory.get('customer')
  const TaxRepository = RepositoryFactory.get('tax')
  const SageSettingsRepository = RepositoryFactory.get('sage_settings')

  function initialState() {
    return {
      open: false,
      invoice_id: '',
      sample_order_id: '',
      invoice_number: '',
      product_order_code: '',
      account_currency: 'GBP',
      fx_value: 1,
      createForm: {
        date: '',
        note: '',
        add_cost: []
      },
      formulation_items: [],
      selectedItems: [],
      tax_codes: [],
      nominal_codes: [],
      sage_departments: [],
      is_table_visible: false,
      customers_order_number: ''
    }
  }

export default {
  name: 'UpdateSampleOrderInvoicePopUp',
  mixins: [SuccessMessage, ErrorMessages, Calculations],
  components: {
    flatPickr
  },
  data () {
    return initialState()
  },
  computed: {
    is_invoice_updatable: function() {
      if (this.selectedItems.length === 0 && (!this.createForm.add_cost || this.createForm.add_cost.length === 0)) {
        return false
      }
      return this.selectedItems.every((x) => !!x.sage_nominal_code_id)
    }
  },
  methods: {
    isItemSelected(item) {
      return this.selectedItems.some((x) => x.sample_order_item_id === item.sample_order_item_id)
    },
    async openPopup (invoice_id, sample_order_id, account_currency = 'GBP', fx_value = 1) {
      this.$vs.loading()
      try {
        Object.assign(this.$data, initialState())
        if (this.$refs.form) {
          this.$refs.form.reset()
        }

        this.invoice_id = invoice_id
        this.sample_order_id = sample_order_id
        this.account_currency = account_currency || 'GBP'
        this.fx_value = fx_value || 1

        // 1. Fetch single invoice data
        const invoiceRes = await CustomerRepository.getSampleOrderInvoice(this.invoice_id)
        const invoiceData = invoiceRes.data.data

        // Check invoice status: only In-Draft can be edited
        const statusName = invoiceData.status ? invoiceData.status.name : ''
        if (!['In-Draft', 'In Draft'].includes(statusName)) {
          this.$vs.notify({
            title: 'Error',
            text: 'Only In-Draft status invoices can be edited.',
            color: 'danger',
            iconPack: 'feather',
            icon: 'icon-alert-circle'
          })
          this.$vs.loading.close()
          return
        }

        this.invoice_number = invoiceData.number || ''
        this.product_order_code = invoiceData.sample_order_code || ''
        this.createForm.date = invoiceData.date || ''
        this.createForm.note = invoiceData.note || ''
        if (invoiceData.invoice_currency) {
          this.account_currency = invoiceData.invoice_currency
        }
        if (invoiceData.fx_value) {
          this.fx_value = invoiceData.fx_value
        }

        // 2. Fetch sample order data
        const targetSampleOrderId = sample_order_id || invoiceData.sample_order_id
        this.sample_order_id = targetSampleOrderId
        const sampleOrderRes = await CustomerRepository.getSampleOrder(targetSampleOrderId)
        const sampleOrderData = sampleOrderRes.data.data

        this.customers_order_number = sampleOrderData.customers_order_number || ''
        if (!this.product_order_code) {
          this.product_order_code = sampleOrderData.sample_order_code || ''
        }

        // 3. Fetch all sample order invoices to compute quantities in other invoices
        const allInvoicesRes = await CustomerRepository.getSampleOrderInvoices(targetSampleOrderId)
        const allInvoices = (allInvoicesRes.data.data || []).filter(
          (x) => x.status && x.status.name !== 'Cancelled' && String(x.id) !== String(this.invoice_id)
        )

        let other_inv_items = []
        allInvoices.forEach((n) => {
          other_inv_items = other_inv_items.concat(n.items || [])
        })

        // Fetch tax codes, nominal codes, and sage departments
        await Promise.all([
          this.fetchTaxCodes(),
          this.fetchNominalCodes(),
          this.getSageDepartments()
        ])

        // Map add_cost from invoice
        this.createForm.add_cost = (invoiceData.add_cost || []).map((n) => {
          return {
            name: n.name || '',
            amount: n.amount !== undefined ? n.amount : (n.amount_ex_vat !== undefined ? n.amount_ex_vat : ''),
            vat_percent: n.vat_percent !== undefined ? n.vat_percent : (n.tax_percent !== undefined ? n.tax_percent : '0.00'),
            sage_nominal_code_id: n.sage_nominal_code ? n.sage_nominal_code.id : (n.sage_nominal_code_id || null),
            sage_department_id: n.sage_department ? (n.sage_department.id || n.sage_department) : (n.sage_department_id || null)
          }
        })

        // 4. Map formulation items from sample order and match with current invoice items
        const rawOrderItems = sampleOrderData.formulation_items || []
        const invItems = invoiceData.items || []

        const mappedItems = []
        const initialSelected = []

        rawOrderItems.forEach((orderItem) => {
          const otherQty = other_inv_items
            .filter((m) => m.formulation_code === orderItem.formulation_code)
            .reduce((sum, b) => sum + Number(b.quantity || 0), 0)

          const totalOrderQty = Number(orderItem.quantity || 0)
          const maxAvailable = Math.max(0, totalOrderQty - otherQty)

          const invItem = invItems.find(
            (m) => m.formulation_code === orderItem.formulation_code || (m.sample_order_item_id && m.sample_order_item_id === orderItem.id)
          )

          let itemObj = {
            sample_order_item_id: orderItem.id,
            formulation_id: orderItem.formulation_id,
            formulation_code: orderItem.formulation_code,
            name: orderItem.name || (invItem ? invItem.formulation_name : ''),
            type: orderItem.type,
            total_quantity: totalOrderQty,
            remaining_to_be_quantity: maxAvailable,
            price: invItem ? invItem.price : orderItem.price,
            selected_quantity: invItem ? Number(invItem.quantity) : maxAvailable,
            tax_code: invItem && invItem.tax_code ? invItem.tax_code : 'Z',
            tax_percent: invItem && invItem.tax_percent !== undefined ? invItem.tax_percent : '0.00',
            sage_nominal_code_id: invItem && invItem.sage_nominal_code ? invItem.sage_nominal_code.id : (invItem ? invItem.sage_nominal_code_id : null),
            sage_department_id: invItem && invItem.sage_department ? (invItem.sage_department.id || invItem.sage_department) : (invItem && invItem.sage_department_id ? invItem.sage_department_id : null)
          }

          if (invItem) {
            initialSelected.push(itemObj)
          }

          mappedItems.push(itemObj)
        })

        // Generate nominal code for any items missing one
        await this.generateNominalCode(sampleOrderData.country, mappedItems)

        this.formulation_items = mappedItems
        this.selectedItems = initialSelected
        this.is_table_visible = true
        this.open = true
      } catch (error) {
        this.convertAndNotifyError(error)
      } finally {
        this.$vs.loading.close()
      }
    },
    async getSageDepartments () {
      try {
        const { data } = await SageSettingsRepository.getListOfSageDepartments('', '', '', 0)
        this.sage_departments = data.data || data
      } catch (error) {
        console.error(error)
      }
    },
    async fetchTaxCodes () {
      try {
        const { data } = await TaxRepository.getListOfTaxCodes(1, '', 150)
        this.tax_codes = data.data || data
      } catch (e) {
        this.convertAndNotifyError(e)
      }
    },
    async fetchNominalCodes () {
      try {
        const { data } = (await SageSettingsRepository.getListOfSageNominalCodes(1, '', 150, 0)).data
        this.nominal_codes = data.data || data
      } catch (error) {
        console.error(error)
      }
    },
    async generateNominalCode (country, formulation_items) {
      try {
        for (let index = 0; index < formulation_items.length; index++) {
          const element = formulation_items[index]
          if (!element.sage_nominal_code_id) {
            const { data } = (await SageSettingsRepository.getListOfSageNominalCodes(1, `filter[formulation_type]=${element.type || 'powder'}&filter[country]=${country}`, 150, 0)).data
            const nominal_list = data.data || data
            const order_item_nominal_code_list = Array.isArray(nominal_list) ? nominal_list.filter((x) => x.is_for_surcharge === false) : []
            if (order_item_nominal_code_list.length > 0) {
              element.sage_nominal_code_id = order_item_nominal_code_list[0].id
            }
          }
        }
      } catch (e) {
        console.error(e)
      }
    },
    calculateNetValue (item) {
      const quantityInKg = Number(item.selected_quantity) / 1000
      const unitPrice = Number(this.$options.filters.format_price(item.price, this.fx_value))
      return (quantityInKg * unitPrice).toFixed(2)
    },
    onClickTaxCode (code, percent, item) {
      item.tax_code = code
      item.tax_percent = percent
    },
    onClickAddAddCost () {
      this.createForm.add_cost.push({
        name: '',
        amount: '',
        vat_percent: '0.00',
        sage_nominal_code_id: null,
        sage_department_id: null
      })
    },
    onClickRemoveAddCost (index) {
      this.createForm.add_cost.splice(index, 1)
    },
    async onClickUpdateInvoice () {
      if (this.selectedItems.length === 0 && (!this.createForm.add_cost || this.createForm.add_cost.length === 0)) {
        this.$vs.notify({
          title: 'Warning',
          text: 'Please select at least one item or add an additional cost.',
          color: 'warning',
          iconPack: 'feather',
          icon: 'icon-alert-circle'
        })
        return
      }

      this.$vs.loading()
      try {
        let payload = {
          date: this.createForm.date,
          note: this.createForm.note || '',
          items: this.selectedItems.map((x) => {
            return {
              sample_order_item_id: x.sample_order_item_id,
              quantity: Number(x.selected_quantity),
              price: x.price,
              tax_code: x.tax_code,
              tax_percent: x.tax_percent,
              sage_nominal_code_id: x.sage_nominal_code_id || null,
              sage_department_id: x.sage_department_id || null
            }
          }).filter((x) => Number(x.quantity) > 0),
          add_cost: (this.createForm.add_cost || []).map((n) => {
            return {
              name: n.name,
              amount: Number(n.amount).toFixed(2),
              vat_percent: Number(n.vat_percent).toFixed(2),
              sage_nominal_code_id: n.sage_nominal_code_id || null,
              sage_department_id: n.sage_department_id || null
            }
          })
        }

        await CustomerRepository.updateSampleOrderInvoice(this.invoice_id, payload)
        this.showSuccessMessage('Invoice updated successfully')
        if (this.$parent && typeof this.$parent.refreshData === 'function') {
          await this.$parent.refreshData()
        }
        this.$emit('refreshData')
        this.open = false
      } catch (e) {
        this.convertAndNotifyError(e)
      } finally {
        this.$vs.loading.close()
      }
    }
  }
}
</script>

<style scoped>
  .add-additional-cost-popup >>> .vs-popup {
    min-width: 70% !important;
  }
  .style-chooser .vs__dropdown-menu {
    z-index: 10030 !important;
  }
</style>
